import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth, applyActionCode, signInWithEmailAndPassword } from 'firebase/auth';
import { AuthContext } from '../context/AuthContext';
import { useLogin } from '../hooks/useLogin';
import './EmailVerificationHandler.css';

const EmailVerificationHandler = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('Verifying your email...');
  const navigate = useNavigate();
  const auth = getAuth();
  const { user } = useContext(AuthContext);
  const { login } = useLogin();

  useEffect(() => {
    const handleEmailVerification = async () => {
      const mode = searchParams.get('mode');
      const oobCode = searchParams.get('oobCode');
      const continueUrl = searchParams.get('continueUrl');

      console.log('[Email Verification] URL params:', { mode, oobCode, continueUrl });
      console.log('[Email Verification] All URL params:', Object.fromEntries(searchParams));

      // Check if user is already logged in and verified
      const currentUser = auth.currentUser;
      if (currentUser && currentUser.emailVerified) {
        console.log('[Email Verification] User already verified and logged in, redirecting...');
        setStatus('success');
        setMessage('Email verified and logged in successfully! Redirecting...');
        
        const redirectUrl = decodeURIComponent(continueUrl || '/Onboarding');
        setTimeout(() => {
          navigate(redirectUrl);
        }, 2000);
        return;
      }

      if (mode !== 'verifyEmail' || !oobCode) {
        console.error('[Email Verification] Invalid parameters - mode:', mode, 'oobCode:', oobCode);
        setStatus('error');
        setMessage('Invalid verification link. Please try signing up again.');
        return;
      }

      try {
        // Apply the verification code first
        await applyActionCode(auth, oobCode);
        console.log('[Email Verification] Email successfully verified with code');
        
        setStatus('success');
        setMessage('Email verified successfully! Setting up your profile...');

        // The user should be logged in from the original signup
        // But the email verification link might have logged them out in some browsers
        let currentUser = auth.currentUser;
        console.log('[Email Verification] Current user after verification:', currentUser?.email || 'None');
        
        if (currentUser) {
          console.log('[Email Verification] User is still signed in from signup - proceeding directly!');
          // Reload the user to get updated emailVerified status
          await currentUser.reload();
          
          console.log('[Email Verification] User email verified status:', currentUser.emailVerified);
          console.log('[Email Verification] Current user UID:', currentUser.uid);
          
          // Force token refresh to get updated email_verified claim
          console.log('[Email Verification] Refreshing Firebase token with updated email verification...');
          await currentUser.getIdToken(true); // Force refresh
          
          console.log('[Email Verification] Email verification complete! User can now access the app.');
          setMessage('Email verified successfully! Redirecting...');
          
          // Brief delay then redirect - the Onboarding component will handle the verification check
          setTimeout(() => {
            const redirectUrl = decodeURIComponent(continueUrl || '/Onboarding');
            navigate(redirectUrl);
          }, 1500);
        } else {
          // No current user - this is the normal case for email verification links
          console.log('[Email Verification] No current user found - this is expected for email verification links');
          setStatus('partial');
          setMessage('Email verified successfully! Please log in to continue.');
          
          // Try to extract the email from the verification URL or other sources
          // This would require additional Firebase Admin SDK calls or URL parsing
          setTimeout(() => {
            navigate('/login?verified=true&continueUrl=' + encodeURIComponent(continueUrl || '/Onboarding'));
          }, 2000); // Reduced delay since no auto-login attempt
        }
      } catch (error) {
        console.error('[Email Verification] Error verifying email:', error);
        setStatus('error');
        
        if (error.code === 'auth/invalid-action-code') {
          setMessage('This verification link has expired or has already been used. Please request a new one.');
        } else if (error.code === 'auth/expired-action-code') {
          setMessage('This verification link has expired. Please request a new verification email.');
        } else {
          setMessage('Failed to verify email. Please try again or contact support.');
        }
      }
    };

    handleEmailVerification();
  }, [searchParams, auth, login, navigate]);

  return (
    <div className="email-verification-container">
      <div className="email-verification-card">
        {status === 'verifying' && (
          <div className="verification-loading">
            <div className="spinner"></div>
            <h2>Verifying Your Email</h2>
            <p>{message}</p>
          </div>
        )}
        
        {status === 'success' && (
          <div className="verification-success">
            <div className="success-icon">✅</div>
            <h2>Email Verified!</h2>
            <p>{message}</p>
          </div>
        )}
        
        {status === 'partial' && (
          <div className="verification-partial">
            <div className="partial-icon">⚠️</div>
            <h2>Almost There!</h2>
            <p>{message}</p>
            <button 
              className="button"
              onClick={() => navigate('/login?verified=true')}
            >
              Go to Login
            </button>
          </div>
        )}
        
        {status === 'error' && (
          <div className="verification-error">
            <div className="error-icon">❌</div>
            <h2>Verification Failed</h2>
            <p>{message}</p>
            <div className="error-actions">
              <button 
                className="button"
                onClick={() => navigate('/signup')}
              >
                Try Signing Up Again
              </button>
              <button 
                className="buttonSecondary"
                onClick={() => navigate('/login')}
              >
                Go to Login
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationHandler;